import React, {
  createContext,
  lazy,
  Suspense,
  useEffect,
  useState,
} from "react";
import { QueryClient, QueryClientProvider } from "react-query";
import { ReactQueryDevtools } from "react-query/devtools";
import { Route, Routes, useLocation, useSearchParams } from "react-router-dom";
import "./App.css";
import "./icomoon.css";
import Footer from "./components/Footer/Footer";
import Header from "./components/Header/Header";
import i18n, { userLanguage } from "./i18n/config";
import ScrollToTop from "./components/ScrollToTop/ScrollToTop";
import Loader from "./components/Loader/Loader";
import getEnv, { getGA } from "./utils/getAppEnv";
import i18next from "i18next";

const Home = lazy(() => import("./pages/Home/Home"));
const PageNotFound = lazy(() => import("./pages/PageNotFound/PageNotFound"));
const Catalogue = lazy(() => import("./pages/Catalogue/Catalogue"));
const Mount = lazy(() => import("./pages/Mount/Mount"));
const queryClient = new QueryClient();

interface GlobalStateData {
  rpm: string;
  kg: string;
  noOfMount: string;
  results: Array<any>;
  failSafe: boolean;
  currentView: any;
  currentCategory: string;
  multiSortMeta: { sortField: string; sortOrder: any };
}
interface GlobalCtx extends GlobalStateData {
  updateGlobalCtx: Function;
}
export const GlobalContext = createContext<GlobalCtx>({
  rpm: "",
  kg: "",
  noOfMount: "",
  results: [],
  failSafe: false,
  currentView: "GRID",
  currentCategory: "",
  updateGlobalCtx: () => {},
  multiSortMeta: { sortField: "", sortOrder: -1 },
});

function App() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const paramsLang = searchParams.get("lang");
  const currentView = searchParams.get("currentView")
    ? (searchParams.get("currentView") as "GRID" | "LIST")
    : "GRID";
  const currentCategory = searchParams.get("currentCategory")
    ? (searchParams.get("currentCategory") as string)
    : "";

  const [lang, setLang] = useState(paramsLang ? paramsLang : userLanguage);
  const [GlobalState, setGlobalState] = useState<GlobalStateData>({
    rpm: "",
    kg: "",
    noOfMount: "",
    results: [],
    failSafe: false,
    currentView,
    currentCategory,
    multiSortMeta: { sortField: "", sortOrder: 0 },
  });
  useEffect(() => {
    const currentView = searchParams.get("currentView")
      ? (searchParams.get("currentView") as "GRID" | "LIST")
      : "";
    const currentCategory = searchParams.get("currentCategory")
      ? (searchParams.get("currentCategory") as string)
      : "";
    if (
      GlobalState.currentView !== currentView ||
      GlobalState.currentCategory !== currentCategory
    ) {
      let newObject = {};
      newObject = {
        ...newObject,
        currentView: currentView ? currentView : "GRID",
      };
      newObject = { ...newObject, currentCategory };
      setGlobalState({ ...GlobalState, ...newObject });
    }
  }, [searchParams]);

  useEffect(() => {
    i18n.changeLanguage(lang);
  }, [lang]);

  useEffect(() => {
    (window as any)?.dataLayer?.push({
      environment: getEnv(),
      ...getGA(
        i18next.language,
        window.location.hash.split("?")[0].replace("#/", "")
      ),
    });
  }, [location]);

  return (
    <div className="App">
      <QueryClientProvider client={queryClient}>
        <GlobalContext.Provider
          key={"glob-context"}
          value={{ ...GlobalState, updateGlobalCtx: setGlobalState }}
        >
          <Header lang={lang} setLang={setLang} />
          <Suspense
            fallback={
              <div className="container">
                <Loader />
              </div>
            }
          >
            <ScrollToTop>
              <Routes>
                <Route path="/" element={<Home />}></Route>
                <Route path="/catalogue" element={<Catalogue />}></Route>
                <Route path="/mount/:id" element={<Mount />}></Route>
                <Route path="*" element={<PageNotFound />}></Route>
              </Routes>
            </ScrollToTop>
          </Suspense>
          <Footer />
        </GlobalContext.Provider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </div>
  );
}

export default App;
